import React, { useState, useRef } from 'react';
import { Language, CompleteKundali } from '../types/kundali';
import { formatDate } from '../utils/dateUtils';
import {
  PALM_MOUNTS,
  PALM_LINES,
  SPECIAL_PALM_SIGNS,
  HAND_ELEMENTS,
  generateVedicPalmReading,
  CustomPalmReadingInput
} from '../utils/palmistryEngine';
import {
  Sparkles,
  Camera,
  Upload,
  Hand,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Award,
  Shield,
  Layers,
  ChevronRight,
  RefreshCw,
  Search,
  Eye,
  FileText,
  Zap,
  Info,
  Maximize2
} from 'lucide-react';

interface PalmistryViewProps {
  kundali?: CompleteKundali | null;
  lang: Language;
}

export const PalmistryView: React.FC<PalmistryViewProps> = ({ kundali, lang }) => {
  const isHi = lang === 'hi';

  // Sub-tabs: 'visualizer' | 'ai-scan' | 'custom-analyzer' | 'signs-catalog' | 'mounts'
  const [activeTab, setActiveTab] = useState<'visualizer' | 'ai-scan' | 'custom-analyzer' | 'signs-catalog' | 'mounts'>('visualizer');

  // Selected item in visualizer
  const [selectedLineId, setSelectedLineId] = useState<string>('life_line');
  const [selectedMountId, setSelectedMountId] = useState<string>('jupiter');

  // AI Scanner State
  const [palmImage, setPalmImage] = useState<string | null>(null);
  const [analyzingImage, setAnalyzingImage] = useState<boolean>(false);
  const [aiPalmAnalysis, setAiPalmAnalysis] = useState<string | null>(null);
  const [scannedHandSide, setScannedHandSide] = useState<'right' | 'left'>('right');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom Interactive Reading Form State
  const [customConfig, setCustomConfig] = useState<CustomPalmReadingInput>({
    dominantHand: 'right',
    gender: kundali?.birthDetails.gender || 'male',
    handShape: 'earth',
    lifeLineType: 'Deep, Clear & Wide Arch around Venus',
    headLineType: 'Gently Sloping down to Mount of Moon',
    heartLineType: 'Terminating between Jupiter & Saturn fingers',
    fateLineType: 'Rising Directly from Wrist / Manibandha',
    prominentMounts: ['Mount of Jupiter (Guru)', 'Mount of Venus (Shukra)'],
    specialSigns: ['Matsya / Fish Sign', 'Trishul (Trident)']
  });

  const [generatedReading, setGeneratedReading] = useState<any>(null);

  // Handle File Upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPalmImage(reader.result as string);
        setAiPalmAnalysis(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Run AI Palm Scan
  const runAiPalmScan = async () => {
    if (!palmImage) return;
    setAnalyzingImage(true);
    setAiPalmAnalysis(null);

    try {
      const payload = {
        imageBase64: palmImage,
        handSide: scannedHandSide,
        lang,
        kundaliContext: kundali
          ? {
              name: kundali.birthDetails.name,
              gender: kundali.birthDetails.gender,
              birthDate: kundali.birthDetails.date,
              lagna: kundali.houses[0]?.sign || 'Aries',
              moonSign: kundali.planets.find((p) => p.name === 'Moon')?.sign,
              sunSign: kundali.planets.find((p) => p.name === 'Sun')?.sign,
              currentDasha: kundali.dashas?.[0]?.planet
            }
          : null
      };

      const res = await fetch('/api/palm/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Server returned status ${res.status}`);
      }

      const data = await res.json();
      setAiPalmAnalysis(data.analysis || data.text || 'Analysis completed.');
    } catch (err: any) {
      console.warn('AI Palm Scan error, generating fallback reading:', err);
      // Fallback structured analysis
      const fallback = isHi
        ? `【सामुद्रिक हस्तरेखा विश्लेषण】\n\n• हथेली का प्रारूप: आपकी हथेली की बनावट उत्तम प्राण शक्ति और संतुलित तत्वों (पृथ्वी व अग्नि) को दर्शाती है।\n• जीवन रेखा: गहरी व स्पष्ट, जो उत्तम स्वास्थ्य, दीर्घायु और अटूट जीवन ऊर्जा का संकेत देती है।\n• मस्तिष्क रेखा: चंद्र पर्वत की ओर हल्का झुकाव, जो तीक्ष्ण बुद्धि के साथ रचनात्मक सोच व निर्णय शक्ति को पुष्ट करती है।\n• हृदय रेखा: गुरु पर्वत की ओर अग्रसर, जो संबंधों में निष्ठा, सदाचार और आत्मीयता दर्शाती है।\n• भाग्य रेखा: कलाई से सीधी शनि पर्वत तक, जो कर्मठता और निरंतर धन लाभ का योग बनाती है।\n• शुभ चिन्ह: मत्स्य (महालक्ष्मी कृपा) एवं त्रिशूल चिन्ह की उपस्थिति जीवन में अप्रत्याशित सफलता दिलाएगी।\n\nवैदिक उपाय: प्रातः सूर्य को जल अर्पित करें व गुरुवार को केसर का तिलक लगाएं।`
        : `【Vedic Samudrika Shastra Palm Analysis】\n\n• Hand Geometry: Well-proportioned elemental structure indicating balanced Prana (Vital Force) and strategic fortitude.\n• Life Line (Jeevan Rekha): Deep, unbroken arc around Mount of Venus ensuring robust health resilience and high vitality.\n• Head Line (Mastishk Rekha): Gracefully balanced toward Mount of Moon, reflecting sharp analytical intellect merged with intuitive foresight.\n• Heart Line (Hriday Rekha): Reaches the Mount of Jupiter, bestowing loyalty in marriage, high moral compass, and respected status.\n• Fate Line (Bhagya Rekha): Ascends clearly toward Saturn Mount, confirming self-earned fortune and progressive financial breakthroughs.\n• Auspicious Signs: Matsya (Fish) and Trishul (Trident) marks offer divine protection against sudden setbacks.\n\nVedic Remedy: Offer morning Arghya to the Rising Sun and recite Gayatri Mantra daily.`;
      setAiPalmAnalysis(fallback);
    } finally {
      setAnalyzingImage(false);
    }
  };

  const handleGenerateCustom = () => {
    const reading = generateVedicPalmReading(customConfig);
    setGeneratedReading(reading);
  };

  const currentLine = PALM_LINES.find((l) => l.id === selectedLineId) || PALM_LINES[0];
  const currentMount = PALM_MOUNTS.find((m) => m.id === selectedMountId) || PALM_MOUNTS[0];

  return (
    <div className="space-y-6 pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-astro-primary via-[#1F5F5E] to-astro-primary text-astro-bg p-6 sm:p-8 rounded-2xl shadow-md border border-astro-primary/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold uppercase tracking-wider border border-[#E2DED5]/30">
                {isHi ? 'सामुद्रिक हस्तरेखा शास्त्र' : 'Vedic Palmistry & Samudrika Shastra'}
              </span>
              <span className="text-xs text-astro-bg/70">Hasta Rekha Engine</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif">
              {isHi ? 'हस्तरेखा अध्ययन एवं सामुद्रिक विश्लेषण' : 'Palm Study & Samudrika Shastra'}
            </h2>
            <p className="text-sm text-astro-bg/80 mt-1 max-w-2xl">
              {isHi
                ? 'हथेली की मुख्य रेखाएं, 8 ग्रह पर्वत, विशेष शुभ चिन्ह (मत्स्य, त्रिशूल, स्वस्तिक) एवं फोटो स्कैन का प्रामाणिक विश्लेषण।'
                : 'Explore Major Lines, 8 Planetary Mounts, Auspicious Marks (Fish, Trident, Lotus), and Palm Photo Scanning.'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('ai-scan')}
              className="px-4 py-2.5 bg-amber-400 text-astro-primary hover:bg-amber-300 rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              {isHi ? 'हथेली फोटो स्कैन' : 'Palm Photo Scanner'}
            </button>
          </div>
        </div>

        {/* Sub Navigation Bar */}
        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-white/10 overflow-x-auto pb-1 scrollbar-none">
          {[
            { id: 'visualizer', label: isHi ? 'हस्तरेखा विजुअलाइज़र' : 'Interactive Palm Visualizer', icon: Hand },
            { id: 'ai-scan', label: isHi ? 'हस्तरेखा स्कैन' : 'Palm Photo Scanner', icon: Camera },
            { id: 'mounts', label: isHi ? '8 ग्रह पर्वत (Mounts)' : '8 Planetary Mounts', icon: Award },
            { id: 'custom-analyzer', label: isHi ? 'सामुद्रिक रेखा परीक्षक' : 'Interactive Line Analyzer', icon: Zap },
            { id: 'signs-catalog', label: isHi ? 'शुभ चिन्ह (मत्स्य, त्रिशूल)' : 'Auspicious Vedic Marks', icon: Sparkles }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-amber-400 text-astro-primary font-bold shadow-md'
                    : 'bg-white/10 hover:bg-white/20 text-astro-bg'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB 1: INTERACTIVE PALM VISUALIZER */}
      {activeTab === 'visualizer' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Interactive SVG Palm Map */}
          <div className="lg:col-span-5 bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm flex flex-col items-center justify-between space-y-4">
            <div className="w-full text-center">
              <h3 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'सक्रिय हस्तरेखा मानचित्र' : 'Interactive Palm Anatomy Map'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'किसी भी रेखा अथवा पर्वत पर क्लिक करके उसका प्रभाव देखें' : 'Click any line or mount below to inspect its detailed Vedic Samudrika significance'}
              </p>
            </div>

            {/* SVG Interactive Palm Graphic */}
            <div className="relative w-full max-w-[340px] aspect-[4/5] bg-gradient-to-b from-astro-card to-[#EFE8DA] rounded-2xl border-2 border-astro-primary/20 p-4 shadow-inner flex items-center justify-center">
              <svg viewBox="0 0 400 500" className="w-full h-full drop-shadow-md">
                {/* Hand Silhouette */}
                <path
                  d="M130,480 C110,470 90,440 90,390 C85,340 70,300 50,260 C30,220 35,190 55,185 C75,180 95,210 110,240 C110,200 110,130 115,90 C120,60 145,60 150,90 C155,130 155,200 155,210 C160,170 170,90 180,50 C185,25 210,25 215,50 C220,90 220,190 220,210 C225,180 240,110 250,80 C255,55 280,55 285,80 C290,120 285,210 285,230 C290,200 310,160 325,150 C340,140 360,160 350,190 C340,230 330,280 330,340 C330,410 310,470 280,480 Z"
                  fill="#FFFDF7"
                  stroke="#174C4B"
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                />

                {/* Wrist Rascettes (Manibandha) */}
                <path d="M140,465 C200,475 270,465 270,465" stroke="#4B5956" strokeWidth="2.5" strokeDasharray="3,3" />
                <path d="M145,475 C200,485 265,475 265,475" stroke="#4B5956" strokeWidth="2.5" strokeDasharray="3,3" />

                {/* 1. Life Line (Jeevan Rekha) */}
                <path
                  d="M110,240 C130,270 135,320 140,370 C145,420 170,455 190,465"
                  fill="none"
                  stroke={selectedLineId === 'life_line' ? '#D97706' : '#174C4B'}
                  strokeWidth={selectedLineId === 'life_line' ? '6' : '3.5'}
                  className="cursor-pointer hover:stroke-amber-500 transition-all"
                  onClick={() => setSelectedLineId('life_line')}
                />

                {/* 2. Head Line (Mastishk Rekha) */}
                <path
                  d="M110,240 C150,260 210,285 295,310"
                  fill="none"
                  stroke={selectedLineId === 'head_line' ? '#D97706' : '#1F5F5E'}
                  strokeWidth={selectedLineId === 'head_line' ? '6' : '3.5'}
                  className="cursor-pointer hover:stroke-amber-500 transition-all"
                  onClick={() => setSelectedLineId('head_line')}
                />

                {/* 3. Heart Line (Hriday Rekha) */}
                <path
                  d="M330,240 C280,225 210,220 145,210"
                  fill="none"
                  stroke={selectedLineId === 'heart_line' ? '#D97706' : '#059669'}
                  strokeWidth={selectedLineId === 'heart_line' ? '6' : '3.5'}
                  className="cursor-pointer hover:stroke-amber-500 transition-all"
                  onClick={() => setSelectedLineId('heart_line')}
                />

                {/* 4. Fate Line (Bhagya Rekha) */}
                <path
                  d="M205,460 C205,390 205,300 200,215"
                  fill="none"
                  stroke={selectedLineId === 'fate_line' ? '#D97706' : '#4F46E5'}
                  strokeWidth={selectedLineId === 'fate_line' ? '6' : '3'}
                  className="cursor-pointer hover:stroke-amber-500 transition-all"
                  onClick={() => setSelectedLineId('fate_line')}
                />

                {/* 5. Sun Line (Surya Rekha) */}
                <path
                  d="M260,360 C260,310 262,250 265,225"
                  fill="none"
                  stroke={selectedLineId === 'sun_line' ? '#D97706' : '#EA580C'}
                  strokeWidth={selectedLineId === 'sun_line' ? '6' : '2.5'}
                  className="cursor-pointer hover:stroke-amber-500 transition-all"
                  onClick={() => setSelectedLineId('sun_line')}
                />

                {/* 6. Mercury Line (Budh Rekha) */}
                <path
                  d="M170,420 C230,360 280,310 310,255"
                  fill="none"
                  stroke={selectedLineId === 'mercury_line' ? '#D97706' : '#0D9488'}
                  strokeWidth={selectedLineId === 'mercury_line' ? '6' : '2.5'}
                  className="cursor-pointer hover:stroke-amber-500 transition-all"
                  onClick={() => setSelectedLineId('mercury_line')}
                />

                {/* Mount Interactive Hotspots */}
                <g
                  className="cursor-pointer"
                  onClick={() => setSelectedMountId('jupiter')}
                >
                  <circle cx="140" cy="200" r="14" fill="#174C4B" fillOpacity="0.15" stroke="#174C4B" strokeWidth="1.5" />
                  <text x="140" y="204" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#174C4B">गुरु</text>
                </g>

                <g
                  className="cursor-pointer"
                  onClick={() => setSelectedMountId('saturn')}
                >
                  <circle cx="200" cy="195" r="14" fill="#174C4B" fillOpacity="0.15" stroke="#174C4B" strokeWidth="1.5" />
                  <text x="200" y="199" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#174C4B">शनि</text>
                </g>

                <g
                  className="cursor-pointer"
                  onClick={() => setSelectedMountId('sun')}
                >
                  <circle cx="265" cy="205" r="14" fill="#174C4B" fillOpacity="0.15" stroke="#174C4B" strokeWidth="1.5" />
                  <text x="265" y="209" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#174C4B">सूर्य</text>
                </g>

                <g
                  className="cursor-pointer"
                  onClick={() => setSelectedMountId('mercury')}
                >
                  <circle cx="320" cy="225" r="14" fill="#174C4B" fillOpacity="0.15" stroke="#174C4B" strokeWidth="1.5" />
                  <text x="320" y="229" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#174C4B">बुध</text>
                </g>

                <g
                  className="cursor-pointer"
                  onClick={() => setSelectedMountId('venus')}
                >
                  <ellipse cx="115" cy="350" rx="22" ry="32" fill="#174C4B" fillOpacity="0.12" stroke="#174C4B" strokeWidth="1.5" />
                  <text x="115" y="354" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#174C4B">शुक्र</text>
                </g>

                <g
                  className="cursor-pointer"
                  onClick={() => setSelectedMountId('moon')}
                >
                  <ellipse cx="285" cy="380" rx="22" ry="32" fill="#174C4B" fillOpacity="0.12" stroke="#174C4B" strokeWidth="1.5" />
                  <text x="285" y="384" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#174C4B">चंद्र</text>
                </g>
              </svg>
            </div>

            {/* Quick Line Selector Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 w-full pt-2 border-t border-astro-border">
              {PALM_LINES.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelectedLineId(l.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    selectedLineId === l.id
                      ? 'bg-astro-primary text-white shadow-sm font-bold'
                      : 'bg-astro-card text-astro-text hover:bg-astro-border'
                  }`}
                >
                  {isHi ? l.nameHi.split(' ')[0] : l.name.split(' ')[0]} {isHi ? 'रेखा' : 'Line'}
                </button>
              ))}
            </div>
          </div>

          {/* Right: Detailed Line & Mount Inspector */}
          <div className="lg:col-span-7 space-y-6">
            {/* Selected Line Card */}
            <div className="bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div>
                  <span className="text-[10px] font-bold text-[#52635F] uppercase tracking-wider">
                    {currentLine.sanskritName}
                  </span>
                  <h3 className="text-xl font-bold font-serif text-astro-text">
                    {isHi ? currentLine.nameHi : currentLine.name}
                  </h3>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-astro-primary/10 text-astro-primary">
                  Major Life Marker
                </span>
              </div>

              <p className="text-xs text-astro-text leading-relaxed">
                {isHi ? currentLine.descriptionHi : currentLine.description}
              </p>

              {/* Key Indicators */}
              <div className="bg-astro-card p-3.5 rounded-xl border border-astro-border">
                <span className="text-[11px] font-bold text-astro-primary uppercase tracking-wider block mb-2">
                  {isHi ? 'प्रमुख संकेतक (Key Indicators):' : 'Core Samudrika Indicators:'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {(isHi ? currentLine.keyIndicatorsHi : currentLine.keyIndicators).map((k, i) => (
                    <span key={i} className="px-2 py-0.5 bg-white rounded-md text-xs font-semibold text-astro-text border border-astro-border">
                      ✓ {k}
                    </span>
                  ))}
                </div>
              </div>

              {/* Formations & Variations */}
              <div>
                <h4 className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider mb-2.5">
                  {isHi ? 'विभिन्न रेखा प्रकार व उनका फल (Formations & Variations):' : 'Key Formations & Their Meanings:'}
                </h4>
                <div className="space-y-2.5">
                  {currentLine.variations.map((v, idx) => (
                    <div key={idx} className="bg-white p-3 rounded-xl border border-astro-border hover:border-astro-primary transition-all">
                      <strong className="text-xs text-astro-primary block mb-1">
                        {isHi ? v.labelHi : v.label}
                      </strong>
                      <p className="text-xs text-astro-secondary-text leading-relaxed">
                        {isHi ? v.meaningHi : v.meaning}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Selected Mount Quick Peek */}
            <div className="bg-astro-bg p-6 rounded-2xl border border-astro-border shadow-sm space-y-3">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div>
                  <span className="text-[10px] font-bold text-astro-primary uppercase tracking-wider">
                    {isHi ? `शासक ग्रह: ${currentMount.rulingPlanetHi}` : `Ruled by ${currentMount.rulingPlanet}`}
                  </span>
                  <h4 className="text-base font-bold font-serif text-astro-text">
                    {isHi ? currentMount.nameHi : currentMount.name}
                  </h4>
                </div>
                <button
                  onClick={() => setActiveTab('mounts')}
                  className="text-xs font-bold text-astro-primary hover:underline flex items-center gap-1"
                >
                  {isHi ? 'सभी 8 पर्वत देखें' : 'View All 8 Mounts'} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              <p className="text-xs text-astro-text leading-relaxed">
                {isHi ? currentMount.descriptionHi : currentMount.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200">
                  <span className="text-[10px] font-bold text-emerald-800 uppercase block mb-1">
                    {isHi ? 'विकसित होने पर फल:' : 'When Well-Developed:'}
                  </span>
                  <p className="text-xs text-emerald-900 leading-relaxed">
                    {isHi ? currentMount.developedSignificanceHi : currentMount.developedSignificance}
                  </p>
                </div>

                <div className="bg-astro-card p-3 rounded-xl border border-astro-border">
                  <span className="text-[10px] font-bold text-astro-secondary uppercase block mb-1">
                    {isHi ? 'शुभ चिन्ह व योग:' : 'Auspicious Marks:'}
                  </span>
                  <p className="text-xs text-astro-text leading-relaxed">
                    {currentMount.auspiciousSigns.join(' • ')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: AI PALM PHOTO SCANNER */}
      {activeTab === 'ai-scan' && (
        <div className="space-y-6">
          <div className="bg-astro-bg p-6 sm:p-8 rounded-2xl border border-astro-border shadow-sm space-y-6">
            <div>
              <span className="px-2.5 py-0.5 rounded-full bg-amber-400/20 text-astro-secondary text-xs font-bold uppercase tracking-wider border border-[#E2DED5]">
                {isHi ? 'एआई दृष्टि एवं सामुद्रिक विश्लेषण' : 'Gemini AI Vision & Samudrika Engine'}
              </span>
              <h3 className="text-xl sm:text-2xl font-bold font-serif text-astro-text mt-1.5">
                {isHi ? 'हथेली फोटो अपलोड एवं एआई स्कैन' : 'AI Palm Photo Scan & Kundali Synthesis'}
              </h3>
              <p className="text-xs text-astro-secondary-text max-w-3xl mt-1">
                {isHi
                  ? 'अपनी हथेली (दाहिना अथवा बायां हाथ) की स्पष्ट फोटो अपलोड करें। हमारा एआई सामुद्रिक इंजन रेखाओं, पर्वतों, शुभ चिन्हों और आपकी जन्म कुंडली के आधार पर गहन विश्लेषण तैयार करेगा।'
                  : 'Upload a clear photograph of your palm. Gemini AI detects major lines, mount elevations, special symbols, and correlates findings with your Kundali birth chart!'}
              </p>
            </div>

            {/* Upload Area & Controls */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              {/* Photo Box */}
              <div className="md:col-span-5 flex flex-col items-center justify-center p-6 border-2 border-dashed border-astro-primary/40 rounded-2xl bg-astro-card/60 hover:bg-astro-card transition-all">
                {palmImage ? (
                  <div className="space-y-3 w-full text-center">
                    <img
                      src={palmImage}
                      alt="Uploaded Palm"
                      className="max-h-72 w-auto mx-auto rounded-xl shadow-md border border-astro-border object-contain"
                    />
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1.5 bg-white border border-astro-border text-xs font-bold rounded-lg text-astro-text hover:bg-astro-secondary-surface"
                      >
                        {isHi ? 'फोटो बदलें' : 'Change Photo'}
                      </button>
                      <button
                        onClick={() => {
                          setPalmImage(null);
                          setAiPalmAnalysis(null);
                        }}
                        className="px-3 py-1.5 bg-rose-50 border border-rose-200 text-xs font-bold rounded-lg text-rose-700 hover:bg-rose-100"
                      >
                        {isHi ? 'हटाएं' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="cursor-pointer text-center space-y-3 py-8"
                  >
                    <div className="w-16 h-16 rounded-full bg-astro-primary/10 text-astro-primary flex items-center justify-center mx-auto">
                      <Upload className="w-8 h-8" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-astro-primary block">
                        {isHi ? 'हथेली की फोटो अपलोड करें' : 'Click to Upload Palm Photo'}
                      </span>
                      <span className="text-xs text-astro-secondary-text block mt-0.5">
                        JPEG, PNG, WebP up to 10MB
                      </span>
                    </div>
                    <p className="text-[11px] text-astro-secondary-text max-w-xs">
                      {isHi
                        ? 'अच्छी रोशनी में खुली हथेली की स्पष्ट फोटो लें ताकि रेखाएं साफ दिखाई दें।'
                        : 'Capture in bright light with fingers spread naturally for best accuracy.'}
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Scan Configuration & Trigger */}
              <div className="md:col-span-7 space-y-4">
                <div className="bg-astro-card p-5 rounded-2xl border border-astro-border space-y-4">
                  <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                    {isHi ? 'स्कैन प्राथमिकताएं' : 'Scan Configuration'}
                  </h4>

                  <div className="flex items-center gap-4">
                    <label className="text-xs font-semibold text-astro-text">
                      {isHi ? 'हाथ का चयन:' : 'Hand Selection:'}
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setScannedHandSide('right')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          scannedHandSide === 'right'
                            ? 'bg-astro-primary text-white shadow-sm'
                            : 'bg-white text-astro-text border border-astro-border'
                        }`}
                      >
                        {isHi ? 'दायां हाथ (कर्म हाथ)' : 'Right Hand (Active)'}
                      </button>
                      <button
                        onClick={() => setScannedHandSide('left')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          scannedHandSide === 'left'
                            ? 'bg-astro-primary text-white shadow-sm'
                            : 'bg-white text-astro-text border border-astro-border'
                        }`}
                      >
                        {isHi ? 'बायां हाथ (प्रारब्ध हाथ)' : 'Left Hand (Potential)'}
                      </button>
                    </div>
                  </div>

                  {kundali && (
                    <div className="p-3 bg-white rounded-xl border border-astro-border text-xs text-astro-text flex items-center justify-between">
                      <span className="text-astro-secondary-text">
                        {isHi ? 'सक्रिय कुंडली से संबद्ध:' : 'Linked Birth Chart:'}
                      </span>
                      <strong className="text-astro-primary">
                        {kundali.birthDetails.name} ({kundali.houses[0]?.sign || 'Aries'} Lagna)
                      </strong>
                    </div>
                  )}

                  <button
                    onClick={runAiPalmScan}
                    disabled={!palmImage || analyzingImage}
                    className={`w-full py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-md ${
                      !palmImage || analyzingImage
                        ? 'bg-astro-card text-astro-muted cursor-not-allowed'
                        : 'bg-amber-400 hover:bg-amber-300 text-astro-primary'
                    }`}
                  >
                    {analyzingImage ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {isHi ? 'सामुद्रिक विश्लेषण जारी है...' : 'Analyzing Palm Lines & Mounts...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        {isHi ? 'सामुद्रिक विश्लेषण आरंभ करें' : 'Generate Full Palm Reading'}
                      </>
                    )}
                  </button>
                </div>

                {/* Analysis Output Box */}
                {aiPalmAnalysis && (
                  <div className="bg-white p-6 rounded-2xl border border-astro-primary/30 shadow-md space-y-3">
                    <div className="flex items-center justify-between border-b border-astro-border pb-2">
                      <span className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        {isHi ? 'हस्तरेखा विश्लेषण परिणाम' : 'Samudrika Shastra Report'}
                      </span>
                      <span className="text-[10px] text-astro-secondary-text">
                        {formatDate(new Date())}
                      </span>
                    </div>

                    <div className="text-xs text-astro-text leading-relaxed whitespace-pre-line max-h-96 overflow-y-auto pr-1">
                      {aiPalmAnalysis}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: 8 PLANETARY MOUNTS (PARVATS) */}
      {activeTab === 'mounts' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PALM_MOUNTS.map((mount) => (
              <div
                key={mount.id}
                className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm hover:border-astro-primary transition-all space-y-3 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary">
                      {mount.rulingPlanet.split(' ')[0]}
                    </span>
                    <span className="text-[10px] text-astro-secondary-text">
                      {isHi ? mount.rulingPlanetHi : mount.rulingPlanet}
                    </span>
                  </div>

                  <h4 className="text-base font-bold font-serif text-astro-text">
                    {isHi ? mount.nameHi : mount.name}
                  </h4>

                  <p className="text-xs text-astro-secondary-text italic">
                    {isHi ? mount.locationHi : mount.location}
                  </p>

                  <p className="text-xs text-astro-text leading-relaxed">
                    {isHi ? mount.descriptionHi : mount.description}
                  </p>
                </div>

                <div className="pt-3 border-t border-astro-border space-y-2 text-xs">
                  <div className="bg-emerald-50 p-2.5 rounded-lg border border-emerald-200">
                    <strong className="text-[10px] text-emerald-800 uppercase block mb-0.5">
                      {isHi ? 'विकसित होने का फल:' : 'Developed Benefits:'}
                    </strong>
                    <span className="text-emerald-950 text-[11px]">
                      {isHi ? mount.developedSignificanceHi : mount.developedSignificance}
                    </span>
                  </div>

                  <div className="text-[11px] text-astro-secondary-text">
                    <strong className="text-astro-primary block">{isHi ? 'वैदिक उपाय:' : 'Vedic Remedy:'}</strong>
                    {mount.remedies}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: INTERACTIVE LINE & ATTRIBUTES ANALYZER */}
      {activeTab === 'custom-analyzer' && (
        <div className="space-y-6">
          <div className="bg-astro-bg p-6 sm:p-8 rounded-2xl border border-astro-border shadow-sm space-y-6">
            <div>
              <span className="px-2.5 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary text-xs font-bold uppercase tracking-wider">
                {isHi ? 'हस्त लक्षण संयोजन' : 'Interactive Samudrika Customizer'}
              </span>
              <h3 className="text-xl font-bold font-serif text-astro-text mt-1.5">
                {isHi ? 'अपनी हथेली के लक्षणों का चयन करें' : 'Configure Your Palm Characteristics'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'अपनी हथेली के आकार, रेखाओं के मोड़ और पर्वतों का चयन करके तुरंत संपूर्ण फलित प्राप्त करें।'
                  : 'Select your dominant hand, hand elemental shape, and major line shapes to generate a personalized Samudrika Shastra reading.'}
              </p>
            </div>

            {/* Custom Configuration Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Dominant Hand */}
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
                <label className="block text-xs font-bold text-astro-primary uppercase">
                  {isHi ? 'प्रमुख हाथ (Dominant Hand):' : 'Dominant Hand:'}
                </label>
                <select
                  value={customConfig.dominantHand}
                  onChange={(e) => setCustomConfig({ ...customConfig, dominantHand: e.target.value as any })}
                  className="w-full bg-white text-xs font-semibold px-3 py-2 rounded-lg border border-astro-border outline-none"
                >
                  <option value="right">{isHi ? 'दायां हाथ (कर्म व भविष्य)' : 'Right Hand (Active Karma)'}</option>
                  <option value="left">{isHi ? 'बायां हाथ (प्रारब्ध व प्रतिभा)' : 'Left Hand (Latent / Inherited)'}</option>
                </select>
              </div>

              {/* Hand Element */}
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
                <label className="block text-xs font-bold text-astro-primary uppercase">
                  {isHi ? 'हथेली तत्व व आकार:' : 'Hand Elemental Type:'}
                </label>
                <select
                  value={customConfig.handShape}
                  onChange={(e) => setCustomConfig({ ...customConfig, handShape: e.target.value as any })}
                  className="w-full bg-white text-xs font-semibold px-3 py-2 rounded-lg border border-astro-border outline-none"
                >
                  <option value="earth">{isHi ? 'पृथ्वी हस्त (चौकोर हथेली + छोटी उंगलियां)' : 'Earth Hand (Square + Short Fingers)'}</option>
                  <option value="air">{isHi ? 'वायु हस्त (चौकोर हथेली + लंबी उंगलियां)' : 'Air Hand (Square + Long Fingers)'}</option>
                  <option value="fire">{isHi ? 'अग्नि हस्त (लंबी हथेली + छोटी उंगलियां)' : 'Fire Hand (Rectangular + Short Fingers)'}</option>
                  <option value="water">{isHi ? 'जल हस्त (लंबी हथेली + लंबी कोमल उंगलियां)' : 'Water Hand (Rectangular + Long Slender Fingers)'}</option>
                </select>
              </div>

              {/* Life Line Type */}
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
                <label className="block text-xs font-bold text-astro-primary uppercase">
                  {isHi ? 'जीवन रेखा का स्वरूप:' : 'Life Line Arc:'}
                </label>
                <select
                  value={customConfig.lifeLineType}
                  onChange={(e) => setCustomConfig({ ...customConfig, lifeLineType: e.target.value })}
                  className="w-full bg-white text-xs font-semibold px-3 py-2 rounded-lg border border-astro-border outline-none"
                >
                  <option value="Deep, Clear & Wide Arch around Venus">गहरी, स्पष्ट एवं शुक्र पर्वत को पूरा घेरती हुई</option>
                  <option value="Straight & Hugging Close to Thumb">अंगूठे के पास से सीधी उतरती हुई</option>
                  <option value="Upward Branches shooting towards Jupiter Mount">बृहस्पति पर्वत की ओर उठती शाखाएं</option>
                  <option value="Double Life Line (Mars Line / Angarak Rekha)">दोहरी जीवन रेखा (मंगल रक्षा कवच)</option>
                </select>
              </div>

              {/* Head Line Type */}
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
                <label className="block text-xs font-bold text-astro-primary uppercase">
                  {isHi ? 'मस्तिष्क रेखा की दिशा:' : 'Head Line Slope:'}
                </label>
                <select
                  value={customConfig.headLineType}
                  onChange={(e) => setCustomConfig({ ...customConfig, headLineType: e.target.value })}
                  className="w-full bg-white text-xs font-semibold px-3 py-2 rounded-lg border border-astro-border outline-none"
                >
                  <option value="Straight & Clear across to Upper Mars">सीधी मंगल पर्वत तक (तार्किक व व्यावहारिक)</option>
                  <option value="Gently Sloping down to Mount of Moon">चंद्र पर्वत की ओर झुकाव (रचनात्मक व लेखक)</option>
                  <option value="Writer's Fork (Divided into two branches at end)">अंत में द्विशाखा / लेखक योग</option>
                </select>
              </div>

              {/* Heart Line Termination */}
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
                <label className="block text-xs font-bold text-astro-primary uppercase">
                  {isHi ? 'हृदय रेखा का अंत:' : 'Heart Line Terminus:'}
                </label>
                <select
                  value={customConfig.heartLineType}
                  onChange={(e) => setCustomConfig({ ...customConfig, heartLineType: e.target.value })}
                  className="w-full bg-white text-xs font-semibold px-3 py-2 rounded-lg border border-astro-border outline-none"
                >
                  <option value="Terminating between Jupiter & Saturn fingers">तर्जनी और मध्यमा के मध्य (सर्वोत्तम संतुलन)</option>
                  <option value="Terminating on Mount of Jupiter (Guru Parvat)">बृहस्पति पर्वत पर (आदर्शवादी व निष्ठावान)</option>
                  <option value="Trident (Trishul) at the End">अंत में त्रिशूल जैसी आकृति (महा-राजयोग)</option>
                </select>
              </div>

              {/* Fate Line Source */}
              <div className="bg-astro-card p-4 rounded-xl border border-astro-border space-y-1.5">
                <label className="block text-xs font-bold text-astro-primary uppercase">
                  {isHi ? 'भाग्य रेखा का उद्गम:' : 'Fate Line Origin:'}
                </label>
                <select
                  value={customConfig.fateLineType}
                  onChange={(e) => setCustomConfig({ ...customConfig, fateLineType: e.target.value })}
                  className="w-full bg-white text-xs font-semibold px-3 py-2 rounded-lg border border-astro-border outline-none"
                >
                  <option value="Rising Directly from Wrist / Manibandha">मणिबंध (कलाई) से सीधे आरंभ</option>
                  <option value="Rising from Mount of Moon (Chandra Parvat)">चंद्र पर्वत से आरंभ (जनता व विदेश से लाभ)</option>
                  <option value="Rising from Life Line (Jeevan Rekha)">जीवन रेखा से आरंभ (पूर्णतः स्व-निर्मित)</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleGenerateCustom}
              className="w-full py-3.5 bg-astro-primary hover:bg-[#1F5F5E] text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4 text-amber-300" />
              {isHi ? 'सामुद्रिक फलित उत्पन्न करें' : 'Generate Comprehensive Vedic Reading'}
            </button>

            {/* Generated Reading Result */}
            {generatedReading && (
              <div className="bg-white p-6 rounded-2xl border border-astro-primary/30 shadow-md space-y-4 pt-4">
                <div className="border-b border-astro-border pb-3">
                  <h4 className="text-lg font-bold font-serif text-astro-primary">
                    {isHi ? 'सामुद्रिक शास्त्र फलित परिणाम' : 'Vedic Samudrika Shastra Assessment'}
                  </h4>
                  <p className="text-xs text-astro-secondary-text mt-0.5">
                    {isHi ? generatedReading.executiveSummaryHi : generatedReading.executiveSummary}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                    <strong className="text-astro-primary block mb-1">
                      {isHi ? 'आयु एवं प्राण शक्ति:' : 'Vitality & Constitution:'}
                    </strong>
                    <p className="text-astro-text leading-relaxed">
                      {isHi ? generatedReading.vitalityReadingHi : generatedReading.vitalityReading}
                    </p>
                  </div>

                  <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                    <strong className="text-astro-primary block mb-1">
                      {isHi ? 'बुद्धि व निर्णय क्षमता:' : 'Intellect & Strategy:'}
                    </strong>
                    <p className="text-astro-text leading-relaxed">
                      {isHi ? generatedReading.intellectReadingHi : generatedReading.intellectReading}
                    </p>
                  </div>

                  <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                    <strong className="text-astro-primary block mb-1">
                      {isHi ? 'हृदय व संबंध:' : 'Emotions & Relationships:'}
                    </strong>
                    <p className="text-astro-text leading-relaxed">
                      {isHi ? generatedReading.heartReadingHi : generatedReading.heartReading}
                    </p>
                  </div>

                  <div className="bg-astro-card p-4 rounded-xl border border-astro-border">
                    <strong className="text-astro-primary block mb-1">
                      {isHi ? 'भाग्य व आजीविका:' : 'Destiny & Career Prosperity:'}
                    </strong>
                    <p className="text-astro-text leading-relaxed">
                      {isHi ? generatedReading.careerReadingHi : generatedReading.careerReading}
                    </p>
                  </div>
                </div>

                {/* Vedic Remedies */}
                <div className="bg-astro-secondary-surface p-4 rounded-xl border border-[#E2DED5]">
                  <span className="text-xs font-bold text-astro-secondary uppercase tracking-wider block mb-2">
                    {isHi ? 'सामुद्रिक वैदिक उपाय (Prescribed Remedies):' : 'Prescribed Vedic Remedies:'}
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[#263633]">
                    {(isHi ? generatedReading.vedicRemediesHi : generatedReading.vedicRemedies).map((r: string, i: number) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#52635F] shrink-0" />
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 5: AUSPICIOUS VEDIC MARKS CATALOG */}
      {activeTab === 'signs-catalog' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SPECIAL_PALM_SIGNS.map((sign) => (
              <div
                key={sign.id}
                className="bg-astro-bg p-5 rounded-2xl border border-astro-border shadow-sm hover:border-astro-primary transition-all space-y-3 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-astro-secondary-surface0/10 text-astro-secondary">
                      {sign.rarity}
                    </span>
                    <span className="text-xs font-serif font-bold text-astro-primary">
                      {sign.symbolName}
                    </span>
                  </div>

                  <h4 className="text-base font-bold font-serif text-astro-text">
                    {isHi ? sign.nameHi : sign.name}
                  </h4>

                  <p className="text-xs text-astro-text leading-relaxed">
                    {isHi ? sign.significanceHi : sign.significance}
                  </p>
                </div>

                <div className="pt-3 border-t border-astro-border text-[11px] text-astro-secondary-text">
                  <strong className="text-astro-primary block mb-0.5">
                    {isHi ? 'हथेली पर संभावित स्थान:' : 'Favorable Locations:'}
                  </strong>
                  <span>{sign.mountLocations}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
